import { Navigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Clock, FileSignature, Mail, ArrowRight, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/** Shown to any logged-in non-admin user whose account is not yet `active`. */
export default function VerificationPending() {
  const { user, logout } = useAuth();
  const loc = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#070707] grid place-items-center">
        <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-[#B87333] animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (user.role === "admin" || user.status === "active") return <Navigate to="/account" replace />;

  const status = user.status || "pending_kyc";
  const isKyc = status === "pending_kyc";
  const isAwaiting = status === "awaiting_approval";

  const stageColor = isKyc ? "#B87333" : "#D8B23E";
  const stageBg = isKyc ? "rgba(184,115,51,0.08)" : "rgba(216,178,62,0.08)";

  const stages = [
    { key: "signup", label: "Account created", done: true },
    { key: "kyc", label: "eKYC & signature", done: !isKyc },
    { key: "approval", label: "Admin approval", done: false },
    { key: "active", label: "Full access", done: false },
  ];

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16 md:py-24" data-testid="verification-pending-page">
      <div className="max-w-[820px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="overline text-[#B87333]">/ Account Access</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            {isKyc ? "One step to unlock." : "Almost there."}
          </h1>
          <p className="font-body text-base text-[#8A8A82] max-w-xl mt-4 leading-relaxed">
            {isKyc
              ? "Complete a quick two-minute eKYC and sign the partnership agreements. Once submitted, an admin reviews and activates your account within a working day."
              : "Your eKYC is complete. A Vinci admin is reviewing your documents and will activate your account shortly — you'll get a Welcome email the moment it's live."}
          </p>
        </motion.div>

        {/* Status card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}
          className="mt-12 rounded-sm border p-8 md:p-10" style={{ borderColor: `${stageColor}66`, background: stageBg }}
          data-testid={`status-card-${status}`}>
          <div className="flex items-center gap-4">
            <span className="grid place-items-center w-14 h-14 rounded-full" style={{ background: stageColor, color: "#070707" }}>
              {isKyc ? <FileSignature size={22} /> : <Clock size={22} />}
            </span>
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em]" style={{ color: stageColor }}>
                {isKyc ? "Verification required" : "Awaiting admin approval"}
              </div>
              <div className="font-serif text-2xl md:text-3xl mt-1">
                {isKyc ? "Complete your eKYC" : "Under review"}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <ol className="mt-8 md:mt-10 grid grid-cols-4 gap-3" data-testid="status-timeline">
            {stages.map((s, i) => {
              const isCurrent = (isKyc && s.key === "kyc") || (isAwaiting && s.key === "approval");
              return (
                <li key={s.key} className="relative">
                  <span className={`block h-1 rounded-full ${s.done ? "bg-[#2FB999]" : isCurrent ? "bg-[#B87333]" : "bg-white/10"}`} />
                  <span className={`font-grotesk text-[9px] md:text-[10px] uppercase tracking-[0.14em] mt-2 block ${s.done ? "text-[#2FB999]" : isCurrent ? "text-[#B87333]" : "text-[#6f6f68]"}`}>
                    {String(i + 1).padStart(2, "0")} — {s.label}
                  </span>
                </li>
              );
            })}
          </ol>

          <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
            {isKyc ? (
              <Link to="/verify" data-testid="cta-start-kyc"
                className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">
                Start verification <ArrowRight size={14} />
              </Link>
            ) : (
              <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || "admin@vincitradeasia.com"}?subject=Approval%20check-in`}
                data-testid="cta-contact-admin"
                className="inline-flex items-center gap-2 rounded-full bg-[#F5F5F0] text-black px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">
                <Mail size={14} /> Ping the admin
              </a>
            )}
            <Link to="/account" data-testid="cta-view-account"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 text-[#C9C9C2] px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] hover:text-[#B87333] transition-colors">
              View my account
            </Link>
            <button onClick={logout} data-testid="cta-logout"
              className="inline-flex items-center gap-2 rounded-full border border-white/10 text-[#8A8A82] px-6 py-4 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-white/30 hover:text-[#F5F5F0] transition-colors">
              <LogOut size={13} /> Log out
            </button>
          </div>
        </motion.div>

        {/* What's locked */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.6 }}
          className="mt-12 grid md:grid-cols-3 gap-4">
          {[
            { t: "Shop", d: "Browse and order construction materials at your tier pricing." },
            { t: "Cart & Checkout", d: "Place orders with GST-compliant invoices and priority delivery." },
            { t: "Partner Network", d: "Access the 600+ Vinci partner map with live pricing feeds." },
          ].map((f) => (
            <div key={f.t} className="rounded-sm border border-white/10 p-6 bg-black/40" data-testid={`locked-${f.t.toLowerCase().replace(/\s|&/g, "-")}`}>
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#B87333]" />
                <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333]">Unlocks once active</span>
              </div>
              <div className="font-serif text-xl mt-3">{f.t}</div>
              <p className="font-body text-sm text-[#8A8A82] mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </motion.div>

        <p className="font-body text-xs text-[#6f6f68] mt-10 max-w-lg">
          Signed in as <span className="text-[#C9C9C2]">{user.email}</span>. All shopping and network features
          are temporarily locked. This is a one-time review to keep the marketplace clean and compliant.
        </p>
      </div>
    </div>
  );
}
